'use client';

import React, { useEffect, useState } from 'react';

import { useAllSpeciesAtLocation } from '../../helpers/hooks/fishrules/useAllSpeciesAtLocation';
import { useRegulationBoundaryAtLocation } from '../../helpers/hooks/fishrules/useRegulationBoundaryAtLocation';
import {
  useIntersectionObserver,
  UseIntersectionObserverOptions,
} from '../../helpers/hooks/useIntersectionObserver';
import { toNumber } from '../../helpers/strings/toNumber';
import { ICoordinates } from '../../interfaces/geo';
import {
  GetFishingWater_fishingWater_region,
  GetPlace_entry_region,
} from '../../interfaces/graphql';
import { Heading } from '../Heading/Heading';
import { RegulationLocationCard } from '../RegulationLocationCard/RegulationLocationCard';
import { IOptionType, Select } from '../Select/Select';
import { StaticPlaceMap } from '../StaticPlaceMap/StaticPlaceMap';
import { Text } from '../Text/Text';

import { NoRegulations } from './components/NoRegulations';
import { RegulationDisclaimer } from './components/RegulationDisclaimer';
import { SpeciesRegulations } from './components/SpeciesRegulations';
import { twMerge } from 'tailwind-merge';
import { Container } from '../Container/Container';
import { LicenseSection } from './components/LicenseSection';
import { useIsDesktopScreenWidthOrWider } from '../../helpers/hooks/useScreenWidth';
import { HydrationSafeDate } from '../HydrationSafeDate/HydrationSafeDate';
import { SelectedSpeciesText } from './components/SelectedSpeciesText';

const DEFAULT_SPECIES = 'SCQyR-cP'; // Largemouth bass

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #DDE3E8',
    height: '48px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: '#3F5162',
    fontSize: '2px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
  }),
};

export interface IRegulationSectionProps {
  coordinates: ICoordinates;
  topSpeciesId?: string;
  region: GetFishingWater_fishingWater_region | GetPlace_entry_region | undefined;
  title?: string;
  className?: string;
  showStaticMap?: boolean;
  showLicenceInfo?: boolean;
  intersectionObserverOptions?: UseIntersectionObserverOptions;
  id?: string;
}

export const RegulationSection = ({
  coordinates,
  topSpeciesId = DEFAULT_SPECIES,
  region,
  title = 'Regulations',
  className,
  showStaticMap = false,
  showLicenceInfo = false,
  intersectionObserverOptions = { rootMargin: '500px' },
  id = 'regulations',
}: IRegulationSectionProps) => {
  const [boundaryName, setBoundaryName] = useState<string | undefined>(undefined);
  const [selectedSpecies, setSelectedSpecies] = useState<IOptionType | undefined>(undefined);
  const [isLoadingSpeciesRegulations, setIsLoadingSpeciesRegulations] = useState(false);
  const [isInView, ref] = useIntersectionObserver(intersectionObserverOptions);
  const isDesktopScreenWidthOrWider = useIsDesktopScreenWidthOrWider();

  const { isLoading: isLoadingAllSpecies, allSpecies } = useAllSpeciesAtLocation(
    isInView ? coordinates : undefined,
  );

  const { isLoading: isLoadingBoundaries, boundaries } = useRegulationBoundaryAtLocation(
    isInView ? coordinates : undefined,
  );

  const fishrulesSpeciesId = toNumber(selectedSpecies?.value);

  useEffect(() => {
    if (selectedSpecies === undefined && allSpecies.length > 0) {
      const { name, fishRulesId } =
        allSpecies.find(({ fishbrainIds }) => fishbrainIds.includes(topSpeciesId)) ?? allSpecies[0];
      setSelectedSpecies({ label: name, value: fishRulesId.toString() });
    }
  }, [allSpecies, selectedSpecies, topSpeciesId]);

  const allSpeciesOptions = allSpecies.map(({ name, fishRulesId }) => ({
    value: fishRulesId.toString(),
    label: name,
  }));

  const hasLicenceInfo = region?.licenseInfoUrl;

  return (
    <div
      className="bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
      ref={ref}
      id={id}
      data-testid="Regulations"
    >
      <Container className={twMerge('mt-sm sm:mt-md py-sm', className)}>
        <Heading as="h2" variant="md" className="mb-8">
          {title}
        </Heading>
        <div className="grid grid-cols-4 gap-3 md:grid-cols-12 md:gap-4">
          {isInView && allSpecies.length === 0 && !isLoadingAllSpecies ? (
            <div className="order-2 col-span-4 md:order-1 md:col-span-6">
              <RegulationDisclaimer />
              <NoRegulations />
            </div>
          ) : (
            <div className="order-2 col-span-4 md:order-1 md:col-span-6">
              <RegulationDisclaimer />
              <Text>
                Fishing regulations {region ? `in ${region.name}` : ''} can change throughout the
                year. Make sure to check this page before fishing for the most up to date rules and
                regulations for the current season. Local regulations govern when you can fish, the
                max size of the fish you can keep, how many fish you can keep, and more.
              </Text>
              <Text>
                Below you will see fishing regulations for catching{' '}
                <SelectedSpeciesText
                  isLoading={isLoadingAllSpecies || isLoadingSpeciesRegulations}
                  selectedSpeciesName={
                    selectedSpecies ? (selectedSpecies as { label: string }).label : 'species'
                  }
                />{' '}
                as of{' '}
                <HydrationSafeDate
                  as="span"
                  loaderBackgroundColor="#e5e7eb"
                  loaderForegroundColor="#d1d5db"
                  isLoading={isLoadingAllSpecies}
                  textVariant="none"
                />
                . To view regulations for a different fish species, please click on your preferred
                species in the drop-down.
              </Text>
              <Select
                isDisabled={isLoadingAllSpecies}
                id="fishing-water-rules-species-select"
                inputId="fishing-water-rules-species-select-input"
                value={selectedSpecies}
                options={allSpeciesOptions}
                onChange={(newVal: unknown) => {
                  setSelectedSpecies(newVal as IOptionType);
                }}
                className="md:w-col-3 mt-1 sm:w-1/2"
                label="Select species"
                styles={customStyles}
                aria-label="Select Species"
              />
              <hr className="text-twilight my-10" />
              <SpeciesRegulations
                boundaries={boundaries}
                coordinates={coordinates}
                fishrulesSpeciesId={fishrulesSpeciesId}
                setBoundaryName={setBoundaryName}
                setIsLoadingSpeciesRegulations={setIsLoadingSpeciesRegulations}
              />
            </div>
          )}
          <div className="order-1 col-span-4 col-start-1 mb-3 sm:col-span-3 sm:mb-8 md:top-0 md:order-2 md:col-span-5 md:col-start-8">
            <div className="sticky top-36">
              <RegulationLocationCard
                coordinates={coordinates}
                boundaryName={boundaryName}
                boundaries={boundaries}
                fishrulesSpeciesId={fishrulesSpeciesId}
                className="relative rounded-sm"
                isLoadingBoundaries={isLoadingBoundaries || isLoadingSpeciesRegulations}
              />
              {showLicenceInfo && hasLicenceInfo && isDesktopScreenWidthOrWider && (
                <LicenseSection
                  className="bg-white md:mx-0 md:mt-8 md:max-w-none"
                  region={region}
                />
              )}
              {showStaticMap && <StaticPlaceMap coordinates={coordinates} />}
            </div>
          </div>
        </div>
      </Container>
      {showLicenceInfo && hasLicenceInfo && !isDesktopScreenWidthOrWider && (
        <Container className="mb-fw sm:mt-md">
          <LicenseSection region={region} />
        </Container>
      )}
    </div>
  );
};
